// crashLab — a way to break the app on purpose, to watch it not break.
//
// DEV ONLY. Every entry point returns at once when NODE_ENV is "production", so
// in a real build this file is a handful of dead branches and nothing reads the
// url. In development it lets a person (or a Playwright script) throw inside a
// named boundary, after the page has rendered, and watch the shell, the
// navigator and the last good view stay up while the boundary puts itself back.
//
//   ?crash=stage            throws inside the stage boundary (the open view)
//   ?crash=nav              inside the navigator's
//   ?crash=sheet            inside the next sheet that opens (new folder, move…)
//   ?crash=chunk            a ChunkLoadError inside the stage: two in a row hand
//                           over to ChunkRecovery (one reload, then a page)
//   ?crash=stage,nav        several at once
//   &for=1500|forever       how long the fault lasts (default 1500ms). A boundary
//                           retries at 0.8s, 2.5s, 6s: 1500 recovers on the second
//                           try, "forever" shows the give-up surface
//   &after=2500             wait this long after load before arming (default
//                           2500ms) so there is a real last good view to keep
//   &once=1                 (chunk) only if this tab has not just reloaded for one
//
// From the console: window.__innernetCrash.arm("stage", 3000) · .stop().

import { useSyncExternalStore } from "react";
import { justReloadedForChunk } from "./resilience";

export type CrashKind = "stage" | "nav" | "sheet" | "chunk";
const KINDS: readonly CrashKind[] = ["stage", "nav", "sheet", "chunk"];

const live = process.env.NODE_ENV !== "production";

const armedUntil = new Map<CrashKind, number>();
const listeners = new Set<() => void>();
let version = 0;
let booted = false;

function emit() {
  version++;
  listeners.forEach((l) => l());
}

/** turn a fault on for `ms` (Infinity = until stopped). */
export function armCrash(kind: CrashKind, ms = 1500): void {
  if (!live) return;
  armedUntil.set(kind, ms === Infinity ? Infinity : Date.now() + ms);
  emit();
}

/** turn one fault (or all of them) off. */
export function stopCrash(kind?: CrashKind): void {
  if (!live) return;
  if (kind) armedUntil.delete(kind);
  else armedUntil.clear();
  emit();
}

function isArmed(kind: CrashKind): boolean {
  const until = armedUntil.get(kind);
  return until !== undefined && Date.now() < until;
}

function boot() {
  if (!live || booted || typeof window === "undefined") return;
  booted = true;
  (window as unknown as { __innernetCrash?: unknown }).__innernetCrash = { arm: armCrash, stop: stopCrash, armed: (k: CrashKind) => isArmed(k) };
  let q: URLSearchParams;
  try {
    q = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  const kinds = (q.get("crash") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is CrashKind => (KINDS as readonly string[]).includes(s));
  if (!kinds.length) return;
  const forMs = q.get("for") === "forever" ? Infinity : Number(q.get("for")) || 1500;
  const after = Number(q.get("after")) || 2500;
  const once = q.get("once") === "1";
  for (const k of kinds) {
    setTimeout(() => {
      if (k === "chunk" && once) {
        let store: Storage | null = null;
        try { store = window.sessionStorage; } catch { /* blocked */ }
        if (justReloadedForChunk(store, Date.now())) return;
      }
      armCrash(k, forMs);
    }, after);
  }
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function fault(kind: CrashKind): Error {
  if (kind === "chunk") return Object.assign(new Error("Loading chunk 482 failed. (crash lab)"), { name: "ChunkLoadError" });
  return new Error(`crash lab: ${kind} tripped on purpose`);
}

/** Render this inside a boundary: it renders nothing, and throws while its
 *  fault is armed. Re-mounting it (a boundary's retry) re-reads the clock. */
export function CrashPoint({ kind }: { kind: CrashKind | readonly CrashKind[] }): null {
  boot();
  useSyncExternalStore(subscribe, () => version, () => 0);
  if (!live) return null;
  const kinds = Array.isArray(kind) ? (kind as readonly CrashKind[]) : [kind as CrashKind];
  for (const k of kinds) if (isArmed(k)) throw fault(k);
  return null;
}
